import type { ResearchTree, ResearchNode } from '@smartphone-tycoon/shared-core';
import { GAME_CONSTANTS } from '../config/gameConstants';

// Technology Branch - Component Unlocks
const technologyNodes: ResearchNode[] = [
  // Tier 1
  {
    id: 'tech_cpu_tier2',
    name: 'Advanced Processors',
    description: 'Unlock Tier 2 CPU options with improved performance',
    category: 'technology',
    tier: 1,
    cost: GAME_CONSTANTS.research.technology.cpuTier2.cost,
    duration: GAME_CONSTANTS.research.technology.cpuTier2.duration,
    prerequisites: [],
    position: { x: 100, y: 100 },
    unlocks: [
      {
        type: 'component',
        id: 'cpu_tier2',
        description: 'Unlocks advanced CPU options'
      }
    ]
  },
  {
    id: 'tech_gpu_tier2',
    name: 'Enhanced Graphics',
    description: 'Unlock Tier 2 GPU options for better visual performance',
    category: 'technology',
    tier: 1,
    cost: GAME_CONSTANTS.research.technology.gpuTier2.cost,
    duration: GAME_CONSTANTS.research.technology.gpuTier2.duration,
    prerequisites: [],
    position: { x: 300, y: 100 },
    unlocks: [
      {
        type: 'component',
        id: 'gpu_tier2',
        description: 'Unlocks enhanced GPU options'
      }
    ]
  },
  {
    id: 'tech_battery_efficiency',
    name: 'Battery Technology',
    description: 'Research improved battery efficiency and capacity',
    category: 'technology',
    tier: 1,
    cost: 400,
    duration: 10,
    prerequisites: [],
    position: { x: 500, y: 100 },
    unlocks: [
      {
        type: 'component',
        id: 'battery_tier2',
        description: 'Unlocks improved battery options'
      },
      {
        type: 'stat_improvement',
        id: 'battery_life',
        description: '+10% battery efficiency',
        value: 10
      }
    ]
  },

  // Tier 2
  {
    id: 'tech_cpu_tier3',
    name: 'Quantum Processors',
    description: 'Unlock cutting-edge Tier 3 CPU technology',
    category: 'technology',
    tier: 2,
    cost: 1500,
    duration: 30,
    prerequisites: ['tech_cpu_tier2'],
    position: { x: 100, y: 250 },
    unlocks: [
      {
        type: 'component',
        id: 'cpu_tier3',
        description: 'Unlocks quantum CPU options'
      }
    ]
  },
  {
    id: 'tech_camera_advanced',
    name: 'Advanced Camera Systems',
    description: 'Unlock multi-lens and AI-powered camera technology',
    category: 'technology',
    tier: 2,
    cost: 1200,
    duration: 20,
    prerequisites: ['tech_gpu_tier2'],
    position: { x: 300, y: 250 },
    unlocks: [
      {
        type: 'component',
        id: 'camera_tier2',
        description: 'Unlocks advanced camera systems'
      },
      {
        type: 'feature',
        id: 'ai_photography',
        description: 'AI-enhanced photography features'
      }
    ]
  },
  {
    id: 'tech_5g_advanced',
    name: '5G+ Technology',
    description: 'Research next-generation network connectivity',
    category: 'technology',
    tier: 2,
    cost: 1000,
    duration: 25,
    prerequisites: [],
    position: { x: 500, y: 250 },
    unlocks: [
      {
        type: 'component',
        id: 'network_5g_plus',
        description: 'Unlocks 5G+ network options'
      }
    ]
  },

  // Tier 3
  {
    id: 'tech_neural_processing',
    name: 'Neural Processing Unit',
    description: 'Develop dedicated AI processing hardware',
    category: 'technology',
    tier: 3,
    cost: 3000,
    duration: 45,
    prerequisites: ['tech_cpu_tier3', 'tech_gpu_tier2'],
    position: { x: 200, y: 400 },
    unlocks: [
      {
        type: 'component',
        id: 'npu',
        description: 'Unlocks Neural Processing Units'
      },
      {
        type: 'stat_improvement',
        id: 'ai_performance',
        description: '+25% AI processing speed',
        value: 25
      }
    ]
  },
  {
    id: 'tech_holographic_display',
    name: 'Holographic Display',
    description: 'Revolutionary 3D display technology',
    category: 'technology',
    tier: 3,
    cost: 5000,
    duration: 60,
    prerequisites: ['tech_camera_advanced'],
    position: { x: 400, y: 400 },
    unlocks: [
      {
        type: 'component',
        id: 'display_holographic',
        description: 'Unlocks holographic display technology'
      }
    ]
  }
];

// Design Branch - Customization Options
const designNodes: ResearchNode[] = [
  // Tier 1
  {
    id: 'design_material_matte',
    name: 'Matte Finish',
    description: 'Develop advanced matte finishing techniques for phone bodies',
    category: 'design',
    tier: 1,
    cost: 300,
    duration: 7,
    prerequisites: [],
    position: { x: 100, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_matte',
        description: 'Matte finish option'
      }
    ]
  },
  {
    id: 'design_material_glossy',
    name: 'Glossy Coating Process',
    description: 'Research high-gloss coating technologies for premium feel',
    category: 'design',
    tier: 1,
    cost: 300,
    duration: 7,
    prerequisites: [],
    position: { x: 250, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_glossy',
        description: 'Glossy finish option'
      }
    ]
  },
  {
    id: 'design_material_metallic',
    name: 'Metallic Surface Treatment',
    description: 'Unlock metallic finish options for premium aesthetics',
    category: 'design',
    tier: 1,
    cost: 400,
    duration: 10,
    prerequisites: [],
    position: { x: 400, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_metallic',
        description: 'Metallic finish option'
      }
    ]
  },
  {
    id: 'design_material_glass',
    name: 'Glass Back Technology',
    description: 'Develop glass back manufacturing capabilities',
    category: 'design',
    tier: 1,
    cost: 500,
    duration: 12,
    prerequisites: [],
    position: { x: 550, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_glass',
        description: 'Glass back option'
      }
    ]
  },
  {
    id: 'design_form_factors',
    name: 'Alternative Form Factors',
    description: 'Research foldable and flip phone designs',
    category: 'design',
    tier: 1,
    cost: 800,
    duration: 20,
    prerequisites: [],
    position: { x: 700, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'form_foldable',
        description: 'Foldable phone design'
      },
      {
        type: 'design_option',
        id: 'form_flip',
        description: 'Flip phone design'
      }
    ]
  },
  {
    id: 'design_sizes_extreme',
    name: 'Extreme Sizes',
    description: 'Unlock compact and tablet-sized phone options',
    category: 'design',
    tier: 1,
    cost: 250,
    duration: 5,
    prerequisites: [],
    position: { x: 850, y: 100 },
    unlocks: [
      {
        type: 'design_option',
        id: 'size_compact',
        description: 'Compact phone size (5.0")'
      },
      {
        type: 'design_option',
        id: 'size_tablet',
        description: 'Tablet phone size (7.5")'
      }
    ]
  },

  // Tier 2
  {
    id: 'design_material_titanium',
    name: 'Titanium Alloy Bodies',
    description: 'Develop titanium alloy manufacturing for ultra-premium devices',
    category: 'design',
    tier: 2,
    cost: 800,
    duration: 15,
    prerequisites: ['design_material_metallic'],
    position: { x: 100, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_titanium',
        description: 'Titanium body material'
      }
    ]
  },
  {
    id: 'design_material_ceramic',
    name: 'Ceramic Materials',
    description: 'Research ceramic body technology for premium durability',
    category: 'design',
    tier: 2,
    cost: 700,
    duration: 14,
    prerequisites: ['design_material_glass'],
    position: { x: 250, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_ceramic',
        description: 'Ceramic body material'
      }
    ]
  },
  {
    id: 'design_material_wood',
    name: 'Wood Composite Technology',
    description: 'Develop sustainable wood composite materials',
    category: 'design',
    tier: 2,
    cost: 600,
    duration: 12,
    prerequisites: ['design_material_matte'],
    position: { x: 400, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_wood',
        description: 'Wood composite material'
      }
    ]
  },
  {
    id: 'design_materials_exotic',
    name: 'Exotic Materials',
    description: 'Unlock carbon fiber technology',
    category: 'design',
    tier: 2,
    cost: 1000,
    duration: 15,
    prerequisites: ['design_material_titanium'],
    position: { x: 550, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_carbon_fiber',
        description: 'Carbon fiber body'
      }
    ]
  },
  {
    id: 'design_modular',
    name: 'Modular Design',
    description: 'Develop modular phone architecture',
    category: 'design',
    tier: 2,
    cost: 1500,
    duration: 30,
    prerequisites: ['design_form_factors'],
    position: { x: 700, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'form_modular',
        description: 'Modular phone design'
      },
      {
        type: 'stat_improvement',
        id: 'repair_cost',
        description: '-20% repair costs',
        value: 20
      }
    ]
  },
  {
    id: 'design_ergonomics',
    name: 'Advanced Ergonomics',
    description: 'Research curved and ergonomic designs',
    category: 'design',
    tier: 2,
    cost: 600,
    duration: 10,
    prerequisites: ['design_sizes_extreme'],
    position: { x: 850, y: 250 },
    unlocks: [
      {
        type: 'design_option',
        id: 'design_curved',
        description: 'Curved edge design'
      },
      {
        type: 'stat_improvement',
        id: 'user_satisfaction',
        description: '+15% user satisfaction',
        value: 15
      }
    ]
  },

  // Tier 3
  {
    id: 'design_material_sapphire',
    name: 'Sapphire Glass Technology',
    description: 'Develop sapphire glass manufacturing for ultimate scratch resistance',
    category: 'design',
    tier: 3,
    cost: 1200,
    duration: 20,
    prerequisites: ['design_material_glass', 'design_material_ceramic'],
    position: { x: 200, y: 400 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_sapphire',
        description: 'Sapphire glass material'
      }
    ]
  },
  {
    id: 'design_transparent',
    name: 'Transparent Technology',
    description: 'Develop transparent phone materials',
    category: 'design',
    tier: 3,
    cost: 4000,
    duration: 50,
    prerequisites: ['design_materials_exotic'],
    position: { x: 400, y: 400 },
    unlocks: [
      {
        type: 'design_option',
        id: 'material_transparent',
        description: 'Transparent materials'
      }
    ]
  }
];

// Business Branch - Stat Improvements
const businessNodes: ResearchNode[] = [
  // Tier 1
  {
    id: 'business_rp_generation',
    name: 'Research Efficiency',
    description: 'Improve research point generation rate',
    category: 'business',
    tier: 1,
    cost: 200,
    duration: 7,
    prerequisites: [],
    position: { x: 100, y: 100 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'rp_generation',
        description: '+20% RP generation rate',
        value: 20
      }
    ]
  },
  {
    id: 'business_production_cost',
    name: 'Production Optimization',
    description: 'Reduce manufacturing costs through process improvements',
    category: 'business',
    tier: 1,
    cost: 400,
    duration: 14,
    prerequisites: [],
    position: { x: 300, y: 100 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'production_cost',
        description: '-10% production costs',
        value: 10
      }
    ]
  },
  {
    id: 'business_monthly_costs',
    name: 'Operational Efficiency',
    description: 'Reduce monthly operational expenses',
    category: 'business',
    tier: 1,
    cost: 350,
    duration: 10,
    prerequisites: [],
    position: { x: 500, y: 100 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'monthly_costs',
        description: '-15% monthly costs',
        value: 15
      }
    ]
  },

  // Tier 2
  {
    id: 'business_rp_advanced',
    name: 'Advanced R&D Methods',
    description: 'Significantly boost research capabilities',
    category: 'business',
    tier: 2,
    cost: 800,
    duration: 20,
    prerequisites: ['business_rp_generation'],
    position: { x: 100, y: 250 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'rp_generation_advanced',
        description: '+35% RP generation rate',
        value: 35
      },
      {
        type: 'stat_improvement',
        id: 'research_speed',
        description: '-20% research duration',
        value: 20
      }
    ]
  },
  {
    id: 'business_quality_control',
    name: 'Quality Management',
    description: 'Implement advanced quality control systems',
    category: 'business',
    tier: 2,
    cost: 600,
    duration: 15,
    prerequisites: ['business_production_cost'],
    position: { x: 300, y: 250 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'defect_rate',
        description: '-25% defect rate',
        value: 25
      },
      {
        type: 'stat_improvement',
        id: 'customer_satisfaction',
        description: '+20% customer satisfaction',
        value: 20
      }
    ]
  },
  {
    id: 'business_supply_chain',
    name: 'Supply Chain Mastery',
    description: 'Optimize supply chain and logistics',
    category: 'business',
    tier: 2,
    cost: 1000,
    duration: 25,
    prerequisites: ['business_monthly_costs'],
    position: { x: 500, y: 250 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'component_cost',
        description: '-15% component costs',
        value: 15
      },
      {
        type: 'stat_improvement',
        id: 'delivery_speed',
        description: '+30% delivery speed',
        value: 30
      }
    ]
  },

  // Tier 3
  {
    id: 'business_innovation_lab',
    name: 'Innovation Laboratory',
    description: 'Establish cutting-edge research facilities',
    category: 'business',
    tier: 3,
    cost: 2500,
    duration: 40,
    prerequisites: ['business_rp_advanced'],
    position: { x: 100, y: 400 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'rp_generation_elite',
        description: '+50% RP generation rate',
        value: 50
      },
      {
        type: 'feature',
        id: 'parallel_research',
        description: 'Research 3 nodes simultaneously'
      }
    ]
  },
  {
    id: 'business_automation',
    name: 'Full Automation',
    description: 'Implement AI-driven business automation',
    category: 'business',
    tier: 3,
    cost: 3500,
    duration: 50,
    prerequisites: ['business_quality_control', 'business_supply_chain'],
    position: { x: 400, y: 400 },
    unlocks: [
      {
        type: 'stat_improvement',
        id: 'all_costs',
        description: '-25% all costs',
        value: 25
      },
      {
        type: 'stat_improvement',
        id: 'efficiency',
        description: '+40% overall efficiency',
        value: 40
      }
    ]
  }
];

export const researchTree: ResearchTree = {
  version: '1.0.0',
  branches: [
    {
      id: 'technology',
      name: 'Technology',
      description: 'Unlock new components and enhance technical capabilities',
      nodes: technologyNodes
    },
    {
      id: 'design',
      name: 'Design',
      description: 'Unlock new design options and customization features',
      nodes: designNodes
    },
    {
      id: 'business',
      name: 'Business',
      description: 'Improve efficiency, reduce costs, and boost research',
      nodes: businessNodes
    }
  ]
};